// useChat — 聊天 WebSocket hook
// 管理与某好友的聊天会话：连接、收发消息、历史加载

import { useEffect, useRef, useState, useCallback } from 'react';
import type { ChatMessage, ChatWsServerMessage } from '@kuaichuan/shared';
import { chatWsUrl, getChatHistory } from '../lib/api';
import { useAuthStore } from '../store/authStore';

export interface UseChatResult {
  messages: ChatMessage[];
  connected: boolean;
  sending: boolean;
  send: (type: ChatMessage['type'], content: string) => void;
  loadMore: () => void;
  hasMore: boolean;
  peerTyping: boolean;
}

export function useChat(peerId: string | null): UseChatResult {
  const token = useAuthStore((s) => s.token);
  const me = useAuthStore((s) => s.user);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [connected, setConnected] = useState(false);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [peerTyping, setPeerTyping] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 加载历史消息
  const loadHistory = useCallback(async (before?: number) => {
    if (!token || !peerId) return [];
    try {
      const msgs = await getChatHistory(token, peerId, before, 50);
      if (msgs.length === 0) setHasMore(false);
      return msgs;
    } catch {
      return [];
    }
  }, [token, peerId]);

  // 初始加载 + WebSocket 连接
  useEffect(() => {
    if (!token || !peerId) return;
    let cancelled = false;

    // 1. 加载历史
    (async () => {
      const history = await loadHistory();
      if (!cancelled) setMessages(history);
    })();

    // 2. WebSocket 连接
    const ws = new WebSocket(chatWsUrl(peerId, token));
    wsRef.current = ws;

    ws.onopen = () => { if (!cancelled) setConnected(true); };
    ws.onclose = () => { if (!cancelled) setConnected(false); };
    ws.onmessage = (ev) => {
      if (cancelled) return;
      let msg: ChatWsServerMessage;
      try { msg = JSON.parse(ev.data); } catch { return; }
      switch (msg.type) {
        case 'MESSAGE':
          setMessages((prev) => [...prev, msg.message]);
          break;
        case 'HISTORY':
          setMessages((prev) => [...msg.messages, ...prev]);
          break;
        case 'TYPING':
          setPeerTyping(true);
          if (typingTimer.current) clearTimeout(typingTimer.current);
          typingTimer.current = setTimeout(() => setPeerTyping(false), 3000);
          break;
        case 'ERROR':
          console.warn('[chat error]', msg.message);
          break;
      }
    };

    return () => {
      cancelled = true;
      ws.close();
      if (typingTimer.current) clearTimeout(typingTimer.current);
    };
  }, [token, peerId, loadHistory]);

  // 发送消息
  const send = useCallback((type: ChatMessage['type'], content: string) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    setSending(true);
    ws.send(JSON.stringify({ type: 'SEND', message: { type, content } }));
    setTimeout(() => setSending(false), 200);
  }, []);

  // 加载更多历史
  const loadMore = useCallback(async () => {
    if (!hasMore || messages.length === 0) return;
    const oldest = messages[0]?.createdAt;
    const older = await loadHistory(oldest);
    if (older.length > 0) {
      setMessages((prev) => [...older, ...prev]);
    } else {
      setHasMore(false);
    }
  }, [hasMore, messages, loadHistory]);

  return { messages, connected, sending, send, loadMore, hasMore, peerTyping };
}
